import { useAuth, WithAuthorization } from "../../context";
import { Page } from "../../components/Page";
import { Card } from "../../components/Card";
import { Button, Icon, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { AssigneeHeader } from "../../components/AssigneeHeader";
import * as React from "react";
import { useMemo, useState } from "react";
import { DataTable } from "../../components/DataTable";
import { FaEye, FaPen } from "react-icons/fa";
import { useGetOrganisationQuestionnaires } from "../../hooks/query";
import { QUESTIONNAIRE_STATUSES, showErrorToast, SORT_DIRECTIONS } from "../../utils";
import { hideAppSpinner } from "../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { routes } from "../../config";
import { getRequestedQuestionnaires } from "../DataEntryPlanning/components/OrganizationPlanning/OrganizationPlanning";

function OrganisationQuestionnaires() {
  const { t } = useTranslation("translation", {
    keyPrefix: "organisationQuestionnaires"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const dispatch = useDispatch();
  const toast = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const assignee = {
    name: user.name,
    avatarUrl: user.picture
  };
  const [dataList, setDataList] = useState([]);

  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    queryPageSortBy: `id,${SORT_DIRECTIONS.DESC}`,
    totalElements: 0,
    totalPages: 0
  });

  const manageDataTableState = (newValObject) => {
    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...newValObject
      };
    });
  };

  const {
    totalPages,
    queryPageIndex,
    queryPageSize,
    queryPageSortBy,
    totalElements
  } = dataTableState;

  const {
    isLoading,
    isFetching
  } = useGetOrganisationQuestionnaires({
    page: queryPageIndex, size: queryPageSize, sort: queryPageSortBy
  }, {
    onSuccess: (page) => {
      const pageContent = page?.content ?? [];
      const dataItems = pageContent.map((item) => {
        return {
          id: item?.id,
          description: item?.description,
          period: item.period ? `${item.period.year} ${item.period.quarter}` : "",
          status: item.status,
          mappedStatus: item.status ? globalTranslation(`questionnaireStatus.${item.status}`) : "",
          questionnaires: getRequestedQuestionnaires(item.configurations),
        };
      });

      setDataList(dataItems);
      if (pageContent.length === 0 && page?.number !== 0 && page?.totalElements !== 0 && queryPageIndex > 0) {
        // Case when page size is changed to larger and user has to be redirected to the previous page
        manageDataTableState({
          totalElements: page?.totalElements ?? 0,
          totalPages: page?.totalPages ?? 0,
          queryPageIndex: queryPageIndex - 1
        });
      } else {
        manageDataTableState({
          totalElements: page?.totalElements ?? 0,
          totalPages: page?.totalPages ?? 0
        });
      }
    },
    onError: error => error.response?.status !== 404 ?
      showErrorToast(toast, error.response?.data?.parameters?.message ?? error.message) : null,
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  const columns = useMemo(() => {
      function handleAction(data) {
        const questionnaireId = data.id;
        navigate(`${routes.organisationQuestionnaire}?id=${questionnaireId}`);
      }

      return [
        {
          header: t("gridColumns.description"),
          accessorKey: "description",
          size: 100
        },
        {
          header: t("gridColumns.period"),
          accessorKey: "period",
          size: 50
        },
        {
          header: t("gridColumns.status"),
          accessorKey: "mappedStatus",
          size: 50
        },
        {
          header: t("gridColumns.questionnaires"),
          accessorKey: "questionnaires",
          size: 150
        },
        {
          header: <div style={{ textAlign: "left" }}><b>{t("gridColumns.actions")}</b></div>,
          accessorKey: "action",
          size: 20,
          cell: row => {
            const { row: { original: rowData } } = row;
            const tooltipAction =
              rowData.status === QUESTIONNAIRE_STATUSES.FINALIZED ? t("buttons.review") : t("buttons.edit");

            return <Button
              onClick={() => handleAction(rowData)}
              variant="ghost"
              title={`${tooltipAction} ${t("questionnaire")}`}
            >
              <Icon
                as={rowData.status === QUESTIONNAIRE_STATUSES.FINALIZED ? FaEye : FaPen}
                inlineSize="4"
                blockSize="4"
                color={"green"}
                opacity="0.7"
              />
            </Button>;
          }
        }
      ];
    }, [t, navigate]
  );

  return (
    <Page title={t("page.title")} subTitle={t("page.subTitle")}>
      <Card>
        <AssigneeHeader assignee={assignee} />
        <DataTable
          data={dataList}
          columns={columns}
          isLoading={isLoading || isFetching}
          marginBlockEnd={4}
          containerHeight="var(--chakra-sizes-2xl)"
          serverSidePagination={true}
          queryTotalCount={totalElements}
          queryPageCount={totalPages}
          queryPageIndex={queryPageIndex}
          setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
          queryPageSize={queryPageSize}
          setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
        />
      </Card>
    </Page>
  );
}

export default WithAuthorization(OrganisationQuestionnaires);